import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import {MonCadencier} from './domains/cadencier/MonCadencier.tsx';
import {ClientLanguageProvider, routes} from 'ff-common';
import {Layout} from './views/Layout';
import {PanierView} from './domains/panier/PanierView.tsx';
import {CatalogueView} from './domains/catalogue/CatalogueView.tsx';
import {ProduitView} from './domains/article/FicheArticle/FicheArticleView.tsx';
import {ProduitMarketplaceView} from './domains/article/FicheArticle/FicheArticleMarketplaceView.tsx';
import {Protected} from './domains/auth/Protected.tsx';
import {SearchView} from './domains/debug/SearchView.tsx';
import {SearchRemplacementView} from './domains/debug/SearchRemplacementView.tsx';
import {DebugNomenclatureView} from './domains/debug/DebugNomenclatureView.tsx';
import {MentionsLegales} from './domains/orga/MentionsLegalesView.tsx';
import {ListeView} from './domains/liste/ListeView.tsx';
import {CacheView} from './domains/cache/CacheView.tsx';
import ProfilView from './domains/client/ProfilView.tsx';
import CommandesView from './domains/commande/CommandesView.tsx';
import {AccueilView} from './domains/accueil/AccueilView.tsx';
import SettingsView from './domains/client/SettingsView.tsx';
import BonLivraisonView from './domains/expedition/BonLivraisonView.tsx';
import FacturesAvoirsView from './domains/facture/FacturesAvoirsView.tsx';
import RepresentantsView from './domains/client/RepresentantsView.tsx';
import {SelectClientView} from './domains/auth/SelectClientView.tsx';
import SidebarMenu from './domains/client/menu/SideBarMenu.tsx';
import {EgalimView} from './domains/egalim/EgalimView.tsx';
import {Erreur404} from './components/Erreurs/Erreur404.tsx';
import {CommandeDetailView} from './domains/commande/CommandeDetailView.tsx';
import {ClientApp} from './domains/auth/ClientApp.tsx';
import {AuthErrors, authErrorRoute} from './domains/auth/AuthErrors.tsx';
import {Connection, connectionRoute} from './domains/auth/Connection.tsx';
import {Maintenance, maintenanceRoute} from './domains/auth/Maintenance.tsx';
import {AuthLayout} from './domains/auth/AuthLayout.tsx';
import {RedirectToPanier} from './domains/panier/controllers/RedirectToPanier.tsx';
import {PanierMarketplaceView} from './domains/panier/PanierMarketplaceView.tsx';
import {DebugModaleRemplacementCatalogueView} from './domains/debug/DebugModaleRemplacementCatalogueView.tsx';
import {Paiement1p6Page} from './domains/panier/Paiement1p6Page.tsx';
import {useClient} from './haligator-factory.tsx';
import {useHalState} from '@synako/haligator';
import {CatalogueGrandCompteApp} from './domains/catalogueGrandCompte/CatalogueGrandCompteApp.tsx';
import {CatalogueGrandCompteAccueil} from './domains/catalogueGrandCompte/CatalogueGrandCompteAccueil.tsx';
import {CatalogueGrandCompteSearch} from './domains/catalogueGrandCompte/CatalogueGrandCompteSearch.tsx';
import {DebugProduitMarcheView} from './domains/debug/DebugProduitMarcheView.tsx';
import {Box, Spinner} from '@chakra-ui/react';
import MesProduitsApp from './domains/mes-produits/MesProduitsApp.tsx';
import {MesProduitsUBView} from './domains/mes-produits/MesProduitsUBView.tsx';
import {DevisPersonnaliseContenu} from './domains/devis/DevisPersonnaliseContenu';
import {ListesContenu} from './domains/liste/components/ListesContenu';
import {ProduitsParSaison} from './domains/liste/components/ProduitsParSaison';
import {OperationCommercialeListeView} from './domains/operation_commerciale/OperationCommercialeListeView';
import {OperationCommercialeView} from './domains/operation_commerciale/OperationCommercialeView';
import {useParams} from 'react-router-dom';

// Gestion de la route pour les opérations commerciales
const OperationCommercialeRoute: React.FC = () => {
  const params = useParams();
  const ocId = params['id_operation_commerciale'];
  if (ocId) {
    return <OperationCommercialeView id_operation_commerciale={ocId} />;
  }
  return <OperationCommercialeListeView context='liste' />;
};

/**
 * Routes pages client partagées (chemins relatifs).
 * Utilisé par le routing classique (/ffconnect) et le routing unifié (/ffconnect/c/:client_id).
 */
function clientPageRoutes() {
  return (
    <>
      <Route
        path='*'
        element={<Erreur404 />}
      />
      <Route
        index
        element={
          <Navigate
            to='accueil'
            replace
          />
        }
      />

      {/* Mes Produits parent + redirections */}
      <Route
        path='mes-produits'
        element={
          <Navigate
            to='mes-produits/cadencier'
            replace
          />
        }
      />

      {/* Mes Produits wrapper et sous-routes */}
      <Route element={<MesProduitsApp />}>
        <Route
          path='mes-produits/cadencier'
          element={<MonCadencier />}
        />
        <Route
          path='mes-produits/listes'
          element={<ListesContenu />}
        />
        <Route
          path='mes-produits/listes/:code_liste'
          element={<ListeView />}
        />
        <Route
          path='mes-produits/par-saison'
          element={<ProduitsParSaison />}
        />
        <Route
          path='mes-produits/devis'
          element={<DevisPersonnaliseContenu />}
        />
        <Route
          path='mes-produits/operation-commerciales'
          element={<OperationCommercialeRoute />}
        />
        <Route
          path='mes-produits/operation-commerciales/:id_operation_commerciale'
          element={<OperationCommercialeRoute />}
        />
        <Route
          path='mes-produits/ub-essentielles'
          element={<MesProduitsUBView />}
        />
      </Route>

      {/* Catalogue */}
      <Route
        path='catalogue'
        element={<CatalogueView />}
      />

      {/* Fiche produit */}
      <Route
        path='produit/:code_article'
        element={<ProduitView />}
      />

      {/* Fiche produit marketplace */}
      <Route
        path='produit/:marketplace_id/:ref_tiers'
        element={<ProduitMarketplaceView />}
      />

      {/* Recherche */}
      <Route
        path='recherche'
        element={<CatalogueView />}
      />

      {/* Panier */}
      <Route
        path='panier'
        element={<PanierView />}
      />
      <Route
        path='panier/:date_panier_actif'
        element={<PanierView />}
      />
      <Route
        path='panier/redirect/:date_de_livraison'
        element={<RedirectToPanier />}
      />
      <Route
        path='panier/marketplace/:marketplace_id'
        element={<PanierMarketplaceView />}
      />

      {/* Mentions legales */}
      <Route
        path='mentions_legales'
        element={<MentionsLegales />}
      />

      {/* Cache */}
      <Route
        path='cache'
        element={<CacheView />}
      />

      {/* Accueil */}
      <Route
        path='accueil'
        element={<AccueilView />}
      />

      {/* SideBarMenu de l'espace client */}
      <Route element={<SidebarMenu />}>
        <Route
          path='profil'
          element={<ProfilView />}
        />
        <Route
          path='commandes'
          element={<CommandesView />}
        />
        <Route
          path='factures'
          element={<FacturesAvoirsView />}
        />
        <Route
          path='livraisons'
          element={<BonLivraisonView />}
        />
        <Route
          path='contacts'
          element={<RepresentantsView />}
        />
        <Route
          path='preferences'
          element={<SettingsView />}
        />
        <Route
          path='egalim'
          element={<EgalimView />}
        />
      </Route>

      {/* Commande Détaillée */}
      <Route
        path='commandes/:cle_commande'
        element={<CommandeDetailView />}
      />
    </>
  );
}

/**
 * Routes de debug (chemins relatifs, hors Layout).
 * Montées à la fois sous le routing classique (/ffconnect) et le routing unifié
 * (/ffconnect/c/:client_id) afin de rester accessibles en mode commercial/assistance.
 */
function debugRoutes() {
  return (
    <>
      <Route
        path='inspect/search'
        element={<SearchView />}
      />
      <Route
        path='inspect/remplacement'
        element={<SearchRemplacementView />}
      />
      <Route
        path='inspect/nomenclature'
        element={<DebugNomenclatureView />}
      />
      <Route
        path='inspect/produit-marche'
        element={<DebugProduitMarcheView />}
      />
      <Route
        path='inspect/modale'
        element={<DebugModaleRemplacementCatalogueView />}
      />
    </>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route
        element={<Maintenance />}
        path={maintenanceRoute.path}
      />
      {/* Routes d'authentification */}
      <Route element={<AuthLayout />}>
        <Route
          element={<Connection />}
          path={connectionRoute.path}
        />
        <Route
          element={<AuthErrors />}
          path={authErrorRoute.path}
        />
      </Route>
      <Route
        element={<Protected />}
        errorElement={<Erreur404 />}
      >
        {/* Selection du client sur lequel se connecter */}
        <Route
          path={routes.client.clientSelect.path}
          element={<SelectClientView />}
        />

        {/* Route pour le Catalogue Grand Compte */}
        <Route element={<CatalogueGrandCompteApp />}>
          <Route
            path={routes.client.catalogueGrandCompte.path}
            element={<CatalogueGrandCompteAccueil />}
          />
          <Route
            path={routes.client.catalogueGrandCompteSearch.path}
            element={<CatalogueGrandCompteSearch />}
          />
        </Route>

        {/* Retour de paiement 1P6 — hors preload pour affichage instantané */}
        <Route
          path='/ffconnect/1p6'
          element={<Paiement1p6Page />}
        />

        {/* Routes client classiques (/ffconnect/...) */}
        <Route element={<ClientApp />}>
          <Route
            path={routes.index.path}
            element={<Outlet />}
          >
            {/* Pour debug — hors Layout */}
            {debugRoutes()}

            <Route element={<Layout />}>{clientPageRoutes()}</Route>
          </Route>
        </Route>

        {/* Routing unifié (/ffconnect/c/:client_id/...) — commercial + assistance */}
        <Route
          path='/ffconnect/c/:client_id'
          element={<ClientApp />}
        >
          {/* Pour debug — hors Layout */}
          {debugRoutes()}

          <Route element={<Layout />}>{clientPageRoutes()}</Route>
        </Route>
      </Route>
    </Route>,
  ),
);

function App() {
  const clientHook = useHalState(useClient());
  return (
    <ClientLanguageProvider
      clientHook={clientHook}
      loadingComponent={
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='100vh'
        >
          <Spinner size='xl' />
        </Box>
      }
    >
      <RouterProvider router={router} />
    </ClientLanguageProvider>
  );
}

export default App;
